/**
 * Helpers for converting and formatting validation table cell values.
 */

import {DocumentTableColumn} from "@dex/squeeze-client-ts";
import {ParsingHelper} from "@/util/ParsingHelper";

/** vue3-excel-editor column types used by the validation table. */
export type EditorColumnType = "string" | "number" | "date" | "boolean";

/** Column data types that are rendered and parsed as numbers. */
const NUMERIC_DATA_TYPES = ["amount", "number"];

/**
 * Returns the lower-cased data type of a column (e.g. "amount", "text"), or "" when unknown.
 * @param column
 */
export function getColumnDataType(column?: DocumentTableColumn): string {
	return (column?.dataType || "").toLowerCase();
}

/**
 * Returns true when the column holds numeric values (amount or number).
 * @param column
 */
export function isNumericColumn(column?: DocumentTableColumn): boolean {
	return NUMERIC_DATA_TYPES.includes(getColumnDataType(column));
}

/**
 * Maps a column's data type to the matching vue3-excel-editor column type.
 * @param column
 */
export function getEditorColumnType(column: DocumentTableColumn): EditorColumnType {
	switch (getColumnDataType(column)) {
	case "date":
		return "date";
	case "boolean":
		return "boolean";
	case "amount":
	case "number":
		return "number";
	case "text":
	default:
		return "string";
	}
}

/**
 * Normalizes a cell value depending on its column type. Only numeric columns are transformed.
 * @param column
 * @param value
 */
export function normalizeCellValue(column: DocumentTableColumn, value: string): string {
	return isNumericColumn(column) ? ParsingHelper.normalizeDecimal(value) : value;
}

/**
 * Formats a numeric string as a localized de-DE amount with two fraction digits.
 * Returns "" for blank input and the original value when it cannot be parsed.
 * @param value
 */
export function formatAmountValue(value: string): string {
	if (!value.length) {
		return "";
	}

	const parsedValue = Number.parseFloat(ParsingHelper.normalizeDecimal(value));
	if (Number.isNaN(parsedValue)) {
		return value;
	}

	return parsedValue.toLocaleString("de-DE", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});
}

/**
 * Formats a cell value for display depending on its column type. Only numeric columns are formatted.
 * @param column
 * @param value
 */
export function formatCellValue(column: DocumentTableColumn, value: string): string {
	return isNumericColumn(column) ? formatAmountValue(value) : value;
}
